import * as TYPES from './types';
import http from '@/utils/api';
import httpV2 from '@/utils/apiV2';
import Auth from '@/utils/auth';
import { agreePayload } from '@/utils/deal';

const namespaced = true;
const ARTICLE_COLOR_GRADE_UNAVAILABLE = 'articleColorGradeUnavailable';
const BUYOUT_SIDE_UNKNOWN = 'buyoutSideUnknown';

const isArticleColorGradeUnavailable = data => data && data.error === ARTICLE_COLOR_GRADE_UNAVAILABLE;

const getAvailableArticleColors = data => {
  if (Array.isArray(data.availableColors)) {
    return data.availableColors;
  }

  if (Array.isArray(data.articleValidation && data.articleValidation.availableColors)) {
    return data.articleValidation.availableColors;
  }

  return [];
};

const buildArticleColorGradeUnavailableError = data => ({
  show: true,
  type: ARTICLE_COLOR_GRADE_UNAVAILABLE,
  message: data.ErrorDetails || 'На данный момент данная оценка невозможна. Обратитесь в службу поддержки.',
  articleValidation: data.articleValidation || null,
  availableColors: getAvailableArticleColors(data),
});

const commitArticleColorGradeUnavailable = (commit, data) => {
  if (!isArticleColorGradeUnavailable(data)) {
    return false;
  }

  commit(TYPES.SET_NEXT_STEP, { nextStep: null });
  commit(TYPES.SET_ERROR, buildArticleColorGradeUnavailableError(data));
  return true;
};

const commitBuyoutSideUnknown = (commit, data) => {
  if (!data || data.error !== BUYOUT_SIDE_UNKNOWN) {
    return false;
  }

  commit(TYPES.SET_NEXT_STEP, { nextStep: null });
  commit(TYPES.SET_ERROR, {
    show: true,
    type: BUYOUT_SIDE_UNKNOWN,
    message: data.ErrorDetails || 'Невозможно определить сторону выкупа. Обратитесь в службу поддержки.',
  });
  return true;
};

const isCurrentOrderResponse = (state, epoch, btag) => state.orderEpoch === epoch
  && (!btag || !state.activeOrderBtag || state.activeOrderBtag === btag);

const commitDealResponse = (commit, data) => {
  if (data && Object.prototype.hasOwnProperty.call(data, 'deal')) commit(TYPES.SET_DEAL, data.deal);
};

const commitAssessmentResponse = (commit, data) => {
  commitDealResponse(commit, data);
  if (!data || typeof data !== 'object') {
    return;
  }

  if (Object.prototype.hasOwnProperty.call(data, 'assessment')) {
    commit(TYPES.SET_ASSESSMENT, data.assessment);
  } else if (
    data.status === 'ok' ||
    data.error === 'manualChangeGrade' ||
    data.error === 'scorringRefuse'
  ) {
    commit(TYPES.SET_ASSESSMENT, null);
  }

  if (Object.prototype.hasOwnProperty.call(data, 'assessmentOutcome')) {
    commit(TYPES.SET_ASSESSMENT_OUTCOME, data.assessmentOutcome);
  } else if (data.assessment) {
    commit(TYPES.SET_ASSESSMENT_OUTCOME, null);
  } else if (
    data.status === 'ok' ||
    data.error === 'manualChangeGrade' ||
    data.error === 'scorringRefuse'
  ) {
    commit(TYPES.SET_ASSESSMENT_OUTCOME, null);
  }
};

const getEmptyManualData = () => ({
  chooseColor: null,
  chooseStorage: null,
  newArticle: null,
  newImei: null,
  newPrice: null
});

const state = () => ({
  number: '',
  numberLength: 0,
  refusals: null,
  model: null,
  questions: null,
  questionAnswersAsIs: true,
  answers: [],
  price: 0,
  discount: null,
  ourDiscount: null,
  subsidies: null,
  scorring: null,
  manual: null,
  mData: getEmptyManualData(),
  cSubsidy: null,
  selectDiscount: null,
  grade: '',
  discounts: [],
  loading: false,
  loader: false,
  calculation: false,
  error: {
    show: false,
    type: '',
  },
  scorring_popup: {
    show: false
  },
  errorSearch: '',
  screenThree: {
    show1: true,
    show2: false,
    show3: false,
    show4: false
  },
  clientInfo: null,
  newOrder: null,
  deviceList: null,
  defineModelByIMEI: null,
  smsInfo: {  
    sendCode: true,
    enterCode: false,
    repeatSendCode: false,
    secondsLeft: 0,
    callType: false,
    code: '',
    loader: false,
    info: {
      code: false,
    },
    errors: {
      code: false,
      send: false
    }
  },
  gradeCount: 0,
  verifyType: 'page',
  phoneNumber: null,
  phoneNumberLength: 0,
  deviceIdSecond: null,
  deviceIdSecondLength: 0,
  preliminary: false,
  finalAssessment: false,
  next_step: null,
  orderStatus: null,
  skipInfoModel: false,
  assessment: null,
  assessmentOutcome: null,
  deal: null,
  activeOrderBtag: null,
  orderEpoch: 0
})

const actions = {
  async refreshDeal({ commit, state }, { btag }) {
    const epoch = state.orderEpoch;
    const response = await http.get('/order', {
      headers: { Authorization: Auth.getAuthHeader() }, params: { btag }
    });
    if (!isCurrentOrderResponse(state, epoch, btag)) return;
    commitDealResponse(commit, response.data);
    if (Object.prototype.hasOwnProperty.call(response.data, 'subsidies')) commit(TYPES.SET_SUBSIDIES, response.data);
    return response.data;
  },
  async saveDeal({ commit, state }, data) {
    const epoch = state.orderEpoch;
    const response = await http.put('/mobile/deal', data, {
      headers: { Authorization: Auth.getAuthHeader() }
    });
    if (!isCurrentOrderResponse(state, epoch, data.btag)) throw new Error('Заявка изменилась. Откройте её заново.');
    if (!response.data || response.data.error || !response.data.deal) {
      const error = new Error('Не удалось сохранить условия заявки.');
      error.response = response;
      throw error;
    }
    commitDealResponse(commit, response.data);
    if (Object.prototype.hasOwnProperty.call(response.data, 'subsidies')) commit(TYPES.SET_SUBSIDIES, response.data);
    return response.data;
  },
  async sendUserAccountLink({ commit }, { btag, phone }) {
    try {
      const response = await http.post('/order/user/account/send', {
        contract: btag,
        btag,
        phone
      }, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending SMS link:', error);
      throw error;
    }
  },
  
  // Действие для отправки уведомления о некорректных данных
  async reportUserAccountError({ commit }, { btag }) {
    try {
      const response = await http.post('/order/user/account/notcorrect', {
        contract: btag,
        btag,
      }, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error reporting incorrect data:', error);
      throw error;
    }
  },
  async getOrder ({ commit, state }, data) {
    console.log('getOrder', data);
    console.log('getOrder loader', data && data.loader);
    if (!data) {
      data = {};
    }
    if (data.btag) commit(TYPES.SET_ACTIVE_ORDER, data.btag);
    const epoch = state.orderEpoch;
    if (data.loader === undefined) {
      data.loader = true;
    }
    if (data.loader) {
      commit(TYPES.SET_LOADING, true);
    }
    try {
      const response = await http.get(`/order`, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
        params: data,
      })
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      if (response.data.device) response.data.device.modelOrigin = response.data.device.name;
      if (response.data.btag) commit(TYPES.SET_MODEL, response.data);
      commitDealResponse(commit, response.data);
      commit(TYPES.SET_PRICE, response.data);
      commit(TYPES.SET_GRADE, response.data);
      commit(TYPES.SET_OURDISCOUNT, response.data);
      commit(TYPES.SET_SUBSIDIES, response.data);
      commit(TYPES.SET_SCORRING, response.data);
      commit(TYPES.SET_MANUAL, response.data);
      commit(TYPES.SET_ORDER_STATUS, response.data.status);

      if (data.loader) {
        commit(TYPES.SET_LOADING, false);
      }

      return response.data;
    } catch(e) {
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      console.log(e)
    }
  },

  async getAllRefused ({ commit }, data) {
    try {
      const response = await http.get('/refusal', {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      commit(TYPES.SET_REFUSALS, response.data);
    } catch(e) {
      console.log(e);
    }
  },

  async setEmptyModel({ commit }, data){
    commit(TYPES.SET_DEVICE_LIST, []);
  },
  async setModelIMEI({ commit }, data){
    commit(TYPES.SET_MODEL_IMEI, data);
  },
  async getModelInfo ({ commit }, data) {
    const loader = data["loader"]
    delete data["loader"]
    if (loader){
      commit(TYPES.SET_LOADER, true);
    }
    try {
      const response = await http.post('/mobile/define', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })

      commit(TYPES.SET_DEVICE_LIST, response.data.deviceList);
      commit(TYPES.SET_DEFINE_MODEL_BY_IMEI, response.data.defineModel);
        

      commit(TYPES.SET_LOADER, false);

      return response.data;
    } catch(e) {
      commit(TYPES.SET_LOADER, false);
      commit(TYPES.SET_ERROR_SEARCH, 'Устройство не найдено в системе, проверьте IMEI. Если все верно, обратитесь в чат технической поддержки.');
      console.log(e);
    }
  },

  async getReferralSources ({ commit }, data) {
    try {
      const response = await http.get('/referral-sources', {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })

      return response.data;
    } catch(e) {
      console.log('Ошибка загрузки источников:', e);
      return null;
    }
  },
  async checkPhoneBlocked ({ commit }, { phoneNumber }) {
    try {
      const response = await httpV2.get('/check/phone', {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
        params: {
          phoneNumber
        }
      });

      return response.data;
    } catch(e) {
      console.log('Ошибка проверки телефона:', e);
      return null;
    }
  },
  async startBuyoutCheck ({ commit }, { deviceId }) {
    try {
      const response = await http.post('/mobile/start-check', {
        deviceId
      }, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      });

      return response.data;
    } catch (e) {
      console.log('Ошибка проверки повторного выкупа:', e);

      if (e.response && e.response.data) {
        return {
          ...e.response.data,
          status: e.response.status
        };
      }

      return {
        error: 'buyout_check_unavailable',
        message: 'Buyout check service unavailable',
        status: 503
      };
    }
  },

  async searchModel ({ commit, state }, data) {
    const epoch = state.orderEpoch;
    commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/mobile/create', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      if (!isCurrentOrderResponse(state, epoch)) return;

      if (response.data.ErrorDetails){
        // if(response.data.ErrorDetails === 'Services is not working now!'){
        //   commit(TYPES.SET_ERROR_SEARCH, 'Данное устройство уже прошло оценку, повторите оценку через 3 минуты!');
        // }else{
          commit(TYPES.SET_ERROR_SEARCH, 'Устройство не найдено в системе, проверьте SN. Если все верно, обратитесь в чат технической поддержки.');
        // }

      }else
        commit(TYPES.SET_MODEL, response.data);

      commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      if (!isCurrentOrderResponse(state, epoch)) return;
      commit(TYPES.SET_LOADING, false);
      if (e.response && e.response.data && e.response.data.error === 'invalidInitialContractType') {
        commit(TYPES.SET_ERROR_SEARCH, e.response.data.ErrorDetails || 'Предварительный тип заявки недоступен. Проверьте выбор или обновите страницу.');
        return e.response.data;
      }
      commit(TYPES.SET_ERROR_SEARCH, 'Устройство не найдено в системе, проверьте IMEI. Если все верно, обратитесь в чат технической поддержки.');
      console.log(e);
    }
  },
  async getOrderInfo ({ commit, state }, data) {
    const epoch = state.orderEpoch;
    commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/mobile/order', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      if (!isCurrentOrderResponse(state, epoch, data.contractId)) return;

      if (response.data.ErrorDetails){
        // if(response.data.ErrorDetails === 'Services is not working now!'){
        //   commit(TYPES.SET_ERROR_SEARCH, 'Данное устройство уже прошло оценку, повторите оценку через 3 минуты!');
        // }else{
          commit(TYPES.SET_ERROR_SEARCH, 'Ошибка! Обратитесь в поддержку ТКИ!');
        // }

      }else
        commit(TYPES.SET_MODEL, response.data);
      commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      if (!isCurrentOrderResponse(state, epoch, data.contractId)) return;
      commit(TYPES.SET_LOADING, false);
      commit(TYPES.SET_ERROR_SEARCH, 'Ошибка! Обратитесь в поддержку ТКИ!');
      console.log(e);
    }
  },
  async sendSms ({ commit }, data) {
    commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/sign/send', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      commit(TYPES.SET_LOADING, false);
      return response.data;
      console.log(e);
    }
  },

  async checkCode ({ commit }, data) {
    // commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/sign/check', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      // commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      // commit(TYPES.SET_LOADING, false);
      console.log(e);
      return response.data;
    }
  },
  async getSubsidies ({ commit }, data) {
    // commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/subsidy', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      // commit(TYPES.SET_LOADING, false);
      commit(TYPES.SET_SUBSIDIES, response.data);
      return response.data;
    } catch(e) {
      // commit(TYPES.SET_LOADING, false);
      console.log(e);
      return response.data;
    }
  },
  async setSubsidies ({ commit }, data) {
    // commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/subsidy/apply', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      // commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      // commit(TYPES.SET_LOADING, false);
      console.log(e);
      return response.data;
    }
  },

  async changeModel ({ commit, state }, data) {
    const epoch = state.orderEpoch;
    try {
      const response = await http.put('/mobile/redefine', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      })
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      commit(TYPES.SET_MODEL, response.data);
    } catch(e) {
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      console.log(e);
    }
  },

  async setAnswers ({ commit }, data) {
    commit(TYPES.SET_ANSWERS, data);
  },

  async getQuestions ({ commit }, data) {
    commit(TYPES.SET_LOADING, true);
    commit(TYPES.RESET_QUESTINS);
    try {
      const response = await http.get(`/mobile/questions?contractId=${data}`, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
        
      })
      commit(TYPES.SET_QUESTIONS, response.data);
      commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      console.log(e);
    }
  },

  async getCalc ({ commit, state }, data) {
    const epoch = state.orderEpoch;
    commit(TYPES.SET_CALCULATION, true);
    try {
      commit(TYPES.SET_LOADING, true);
      const response = await http.post('/mobile/calc', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
      })
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;

      if (commitArticleColorGradeUnavailable(commit, response.data)) {
        commit(TYPES.SET_CALCULATION, false);
        commit(TYPES.SET_LOADING, false);
        return response.data;
      }

      commitAssessmentResponse(commit, response.data);

      commit(TYPES.SET_PRICE, response.data);
      commit(TYPES.SET_GRADE, response.data);
      commit(TYPES.SET_DISCOUNTS, response.data);
      commit(TYPES.SET_SUBSIDIES, response.data);
      commit(TYPES.SET_OURDISCOUNT, response.data);
      commit(TYPES.SET_SCORRING, response.data);
      commit(TYPES.SET_MANUAL, response.data);
      commit(TYPES.SET_NEXT_STEP, response.data);
      commit(TYPES.SET_CALCULATION, false);
      commit(TYPES.SET_LOADING, false);
      return response.data;
    } catch(e) {
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      if (e.response && commitArticleColorGradeUnavailable(commit, e.response.data)) {
        commit(TYPES.SET_CALCULATION, false);
        commit(TYPES.SET_LOADING, false);
        return e.response.data;
      }

      if (e.response && commitBuyoutSideUnknown(commit, e.response.data)) {
        commit(TYPES.SET_CALCULATION, false);
        commit(TYPES.SET_LOADING, false);
        return e.response.data;
      }

      commit(TYPES.SET_CALCULATION, false);
      commit(TYPES.SET_LOADING, false);
      console.log(e);
    }
  },
  async setError ({ commit }, error) {
    if (error && typeof error === 'object') {
      commit(TYPES.SET_ERROR, { show: true, ...error })
      return;
    }

    commit(TYPES.SET_ERROR, { show: true, type: error })
  },
  async showScorringPopup ({ commit }) {
    commit(TYPES.SET_SCORRING_POPUP, { show: true})
  },
  async closeScorringPopup ({ commit }) {
    commit(TYPES.SET_SCORRING_POPUP, { show: false})
  },
  async agreeModel ({ commit, state }, data) {
    const epoch = state.orderEpoch;
    commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/mobile/agree', agreePayload(data, state.deal), {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
      });
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;

      if (commitArticleColorGradeUnavailable(commit, response.data)) {
        commit(TYPES.SET_LOADING, false);
        return response.data;
      }

      commitAssessmentResponse(commit, response.data);
      
      commit(TYPES.SET_NEXT_STEP, response.data)
      commit(TYPES.SET_SCORRING, response.data)
      commit(TYPES.SET_MANUAL, response.data)
      commit(TYPES.SET_SUBSIDIES, response.data)
      if (response.data.newPrice){
        commit(TYPES.SET_NEWPRICE, response.data.newPrice)
      } 
      if (commitBuyoutSideUnknown(commit, response.data)) {
        commit(TYPES.SET_LOADING, false);
        return response.data;
      }
      if (
        response.data.error &&
        response.data.error !== 'assessmentRequired' &&
        response.data.error !== 'preliminaryAssessment' &&
        !(response.data.error === 'manualChangeGrade' && response.data.assessmentOutcome)
      )
        commit(TYPES.SET_ERROR, { show: true, type: response.data.error })
      // else
        // commit(TYPES.CHANGE_SCREEN_THREE, { show1: false, show2: true });

        

      commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      if (e.response && commitArticleColorGradeUnavailable(commit, e.response.data)) {
        commit(TYPES.SET_LOADING, false);
        return e.response.data;
      }

      if (e.response) {
        const responseData = e.response.data;
        commitAssessmentResponse(commit, responseData);
        commitBuyoutSideUnknown(commit, responseData);
        commit(TYPES.SET_LOADING, false);
        return responseData;
      }

      commit(TYPES.SET_LOADING, false);
      console.log(e);
    }
  },
  async checkStatusScorring ({ commit, state }, data) {
    const epoch = state.orderEpoch;
    try {
      const response = await http.post('/mobile/agree', agreePayload(data, state.deal), {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
      });
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      commitAssessmentResponse(commit, response.data);
      commit(TYPES.SET_NEXT_STEP, response.data);
      commit(TYPES.SET_SCORRING, response.data);
      commit(TYPES.SET_MANUAL, response.data);
      commit(TYPES.SET_SUBSIDIES, response.data);
      if (response.data.newPrice){
        commit(TYPES.SET_NEWPRICE, response.data.newPrice)
      } 
      return response.data;
    } catch(e) {
      if (!isCurrentOrderResponse(state, epoch, data.btag)) return;
      if (e.response) {
        const responseData = e.response.data;
        commitAssessmentResponse(commit, responseData);
        if (Object.prototype.hasOwnProperty.call(responseData, 'nextStep')) {
          commit(TYPES.SET_NEXT_STEP, responseData);
        }
        if (Object.prototype.hasOwnProperty.call(responseData, 'scorring')) {
          commit(TYPES.SET_SCORRING, responseData);
        }
        if (Object.prototype.hasOwnProperty.call(responseData, 'manual')) {
          commit(TYPES.SET_MANUAL, responseData);
        }
        if (Object.prototype.hasOwnProperty.call(responseData, 'subsidies')) {
          commit(TYPES.SET_SUBSIDIES, responseData);
        }
        if (responseData.newPrice) {
          commit(TYPES.SET_NEWPRICE, responseData.newPrice);
        }
        commitBuyoutSideUnknown(commit, responseData);
        return responseData;
      }
      console.log(e);
    }
  },
  async changeDiscount ({ commit }, data) {
    commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.post('/discount/apply', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
      });
      commit(TYPES.SET_LOADING, false);

      return response.data;
    } catch(e) {
      commit(TYPES.SET_LOADING, false);
      console.log(e);
    }
  },
  async updatePrice ({ commit }, price) {
     commit(TYPES.SET_PRICE, {"price": price});
  },
  async clearDiscount ({ commit },discountName) {
    commit(TYPES.SELECT_DISCOUNT,discountName);
    commit(TYPES.CLEAR_DISCOUNT);
  },
  async updateOrder ({ commit }, data) {
    commit(TYPES.SET_LOADING, true);
    try {
      const response = await http.put('/order', data.data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
        params: {
          btag: data.btag,
        }
      });

      commit(TYPES.SET_LOADING, false);

      if (response.data)
        commit(TYPES.SET_ORDER, response.data);
    } catch(e) {
      commit(TYPES.SET_LOADING, false);
      console.log(e);
    }
  },

  async markContractReady ({ commit }, { btag }) {
    try {
      const response = await http.put('/order/ready', {
        btag
      }, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        }
      });

      return response.data;
    } catch(e) {
      console.log(e);
      throw e;
    }
  },

  async cancelModel ({ commit }, data) {
    try {
      const response = await http.post('/mobile/refused', data, {
        headers: {
          'Authorization': Auth.getAuthHeader()
        },
      });
      return true;
    } catch(e) {
      console.log(e);
    }
  },

  async chooseSubsidies ({commit}, data){
    commit(TYPES.SET_CHOOSESUBSIDIES, data)
  },
  async saveManualData ({commit}, data){
    commit(TYPES.SET_MANUALDATA, data)
  },
  changeScreenThree ({commit}, data){
    commit(TYPES.CHANGE_SCREEN_THREE, data)
  }
}

const mutations = {
  [TYPES.SET_ACTIVE_ORDER](state, btag) {
    if (state.activeOrderBtag !== btag) {
      state.activeOrderBtag = btag;
      state.orderEpoch += 1;
      state.calculation = false;
    }
  },
  [TYPES.SET_DEAL](state, payload) {
    // A slower background read must not roll back a newer save/status response.
    if (state.deal && (!payload || payload.revision < state.deal.revision)) return;
    state.deal = payload || null;
  },
  [TYPES.SET_ORDER](state, payload) {
    state.newOrder = payload;
  },

  [TYPES.CHANGE_CLIENT_INFO](state, payload) {
    state.clientInfo = payload;
  },

  [TYPES.CHANGE_SCREEN_THREE](state, payload) {
    for (let key in payload) {
      state.screenThree[key] = payload[key];
    }
  },

  [TYPES.SET_ERROR_SEARCH](state, payload) {
    state.errorSearch = payload;
  },

  [TYPES.SET_ERROR](state, payload) {
    state.error = payload;
  },
  [TYPES.SET_SCORRING_POPUP](state, payload) {
    state.scorring_popup = payload;
  },

  [TYPES.SET_NEXT_STEP](state, payload) {
    console.log(payload);
    console.log(payload && payload.nextStep);
    if (payload && payload.nextStep) {
      state.next_step = payload.nextStep;
    } else {
      state.next_step = null;
    }
  },

  [TYPES.SET_LOADING](state, payload) {
    state.loading = payload;
  },

  [TYPES.SET_DEVICE_LIST](state, payload) {
    state.deviceList = {
      data: payload,
      manufactures: Object.keys(payload),
      models: [],
      storages: []
    };

  },
  [TYPES.SET_MODEL_IMEI](state, payload) {
    state.model.imei = payload
  },
  [TYPES.SET_MODELS_LIST](state, payload) {
    state.deviceList.models = payload

  },
  [TYPES.SET_STORAGES_LIST](state, payload) {
    state.deviceList.storages = payload

  },

  [TYPES.SET_DEFINE_MODEL_BY_IMEI](state, payload) {
    state.defineModelByIMEI = payload;
    try{
      if (payload){
        state.deviceList.models = Object.keys(state.deviceList.data[payload.manufacture]);
        state.deviceList.storages = Object.keys(state.deviceList.data[payload.manufacture][payload.model]);
      }
    }catch(err){
      console.log(err)
    }
    
  },
  
  [TYPES.SET_LOADER](state, payload) {
    state.loader = payload;
  },
  [TYPES.SET_CALCULATION](state, payload) {
    state.calculation = payload;
  },
  
  [TYPES.SET_NUMBER](state, payload) {
    state.number = payload.number;
    state.numberLength = payload.number.length;
  },
  [TYPES.SET_DEVICEID_SECOND](state, payload) {
    state.deviceIdSecond = payload.number;
    state.deviceIdSecondLength = payload.number.length;
  },

  

  [TYPES.SET_PHONENUMBER](state, payload) {
    state.phoneNumber = payload.phoneNumber;
    state.phoneNumberLength = payload.length;
  },

  [TYPES.SET_PRELIMINARY](state, payload) {
    state.preliminary = Boolean(payload);
  },

  [TYPES.SET_FINAL_ASSESSMENT](state, payload) {
    state.finalAssessment = Boolean(payload);
  },
  

  [TYPES.SET_REFUSALS](state, payload) {
    state.refusals = payload;
  },

  [TYPES.SET_PRICE](state, payload) {
    state.price = payload && Object.prototype.hasOwnProperty.call(payload, 'price') ? payload.price : 0;
  },
  [TYPES.SET_SUBSIDIES](state, payload) {
    if (payload && payload.subsidies){
      state.subsidies = payload.subsidies;
    } else {
      state.subsidies = null;
    }
    
  },
  [TYPES.SET_OURDISCOUNT](state, payload) {
    state.ourDiscount = payload && payload.ourDiscount ? payload.ourDiscount : null;
  },
  [TYPES.SET_CHOOSESUBSIDIES](state, data) {
    state.cSubsidy = data;
  },
  
  [TYPES.SET_SCORRING](state, payload) {
    state.scorring = payload && Object.prototype.hasOwnProperty.call(payload, 'scorring') ? payload.scorring : null;
    state.verifyType = payload && payload.verifyType ? payload.verifyType : 'page';
  },
  [TYPES.SET_MANUAL](state, payload) {
    if (!payload || !Object.prototype.hasOwnProperty.call(payload, 'manual')) {
      state.manual = null;
      return;
    }

    state.manual = payload.manual;
  },
  [TYPES.SET_ORDER_STATUS](state, payload) {
    state.orderStatus = payload;
  },
  [TYPES.SET_SKIP_INFO_MODEL](state, payload) {
    state.skipInfoModel = Boolean(payload);
  },
  [TYPES.SET_ASSESSMENT](state, payload) {
    if (!payload || !payload.id || !payload.type || !payload.status) {
      state.assessment = null;
      return;
    }

    state.assessment = {
      id: payload.id,
      type: payload.type,
      status: payload.status,
    };
  },
  [TYPES.SET_ASSESSMENT_OUTCOME](state, payload) {
    state.assessmentOutcome = payload || null;
  },
  [TYPES.SET_MANUALDATA](state, data) {
    state.mData = data;
  },
  [TYPES.SET_NEWPRICE](state, data) {
    if (!data) {
      state.price = 0;
      state.grade = '';
      state.ourDiscount = null;
      state.discount = 0;
      return;
    }

    state.price = data.price;
    state.grade = data.grade || '';
    if (data.ourDiscount){
      state.ourDiscount = data.ourDiscount;
    }else{
      state.ourDiscount = null;
    }
    if (data.discount){
      state.discount = data.discount;
    }else{
      state.discount = 0;
    }
    
  },
  
  
  [TYPES.SET_GRADE](state, payload) {
    state.grade = payload && payload.grade ? payload.grade : ''
  },
  [TYPES.SET_DISCOUNTS](state, payload) {
    if (payload && Array.isArray(payload.discounts)){
      state.discounts = payload.discounts
    } else {
      state.discounts = []
    }
  },
  [TYPES.CLEAR_DISCOUNT](state) {
    state.discounts = []
  },
  [TYPES.SELECT_DISCOUNT](state, discountName) {
    state.selectDiscount = discountName
  },


  [TYPES.SET_MODEL](state, payload) {
    let model = {};
    if (payload.device) {
      for (let key in payload) {
        if (key == 'device') {
          model['mobile'] = payload[key];
          for (let key2 in payload[key]) {
            if (key2 == 'name') {
              model['mobile']['ModelName'] = payload[key][key2];
            } else
              model['mobile'][key2] = payload[key][key2];
          }
        } else
          model[key] = payload[key];
      }
    } else{
      model = payload;
    }
    const changedOrder = model.btag && (!state.model || state.model.btag !== model.btag);
    if (changedOrder) {
      state.deal = null;
      state.calculation = false;
      if (state.activeOrderBtag !== model.btag) {
        state.activeOrderBtag = model.btag;
        state.orderEpoch += 1;
      }
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'deal')) {
      if (!state.deal || (payload.deal && payload.deal.revision >= state.deal.revision)) state.deal = payload.deal;
    }
    state.model = model;
  },

  [TYPES.RESET_PHONES](state, payload) {
    state.activeOrderBtag = null;
    state.orderEpoch += 1;
    state.loading = false;
    state.calculation = false;
    state.model = null;
    state.number = '';
    state.numberLength = 0;
    state.questions = null;
    state.answers = [];
    state.price = 0;
    state.discount = null;
    state.ourDiscount = null;
    state.subsidies = null;
    state.scorring = null;
    state.manual = null;
    state.mData = getEmptyManualData();
    state.cSubsidy = null;
    state.selectDiscount = null;
    state.grade = '';
    state.discounts = [];
    state.phoneNumber = null;
    state.phoneNumberLength = 0;
    state.deviceIdSecond = null;
    state.deviceIdSecondLength = 0;
    state.deviceList = null;
    state.questionAnswersAsIs = true;
    state.defineModelByIMEI = null;
    state.errorSearch = '';
    state.loader = false;
    state.preliminary = false;
    state.finalAssessment = false;
    state.next_step = null;
    state.orderStatus = null;
    state.verifyType = 'page';
    state.screenThree = {
      show1: true,
      show2: false,
      show3: false,
      show4: false
    };
    state.clientInfo = null;
    state.newOrder = null;
    state.skipInfoModel = false;
    state.assessment = null;
    state.assessmentOutcome = null;
    state.deal = null;
  },

  [TYPES.SET_QUESTIONS](state, payload) {
    let questions = payload.groups;
    let answers = [];
    questions.map((item, i) => {
      if (i == 0) {
        item.active = true;
      } else {
        item.active = false
      }
      item.questions.map((item2, i2) => {
        if (i == 0 && i2 == 0) {
          item2.active = true;
        } else {
          item2.active = false;
        }
        answers.push({
          id: item2.id,
          answer: false,
        })
      })
    })
    state.questions = questions;
    state.questionAnswersAsIs = payload.questionAnswersAsIs !== false;
    state.gradeCount = payload.gradeCount;
    state.answers = answers;
  },
  [TYPES.SET_ANSWERS](state, payload) {
    state.answers = payload;
  },
  [TYPES.RESET_QUESTINS](state, payload) {
    state.questions = null;
    state.questionAnswersAsIs = true;
    state.answers = [];
  },

  [TYPES.ADD_ANSWER](state, payload) {
    let itemNum, questionNum, questionId;
    state.questions.map((item, i) => {
      if (item.active == true) {
        item.active = false;
        itemNum = parseInt(i);

        item.questions.map((item2, i2) => {
          if (item2.active == true) {
            item2.active = false;
            questionNum = parseInt(i2);
            questionId = item2.id;
          }
        })
      }
    })

    if (payload.type == 'next') {
      if (state.questions[itemNum].questions[questionNum+1]) {
        state.questions[itemNum].questions[questionNum+1].active = true;
        state.questions[itemNum].active = true;
      } else {
        if (state.questions[itemNum+1]) {
          state.questions[itemNum+1].active = true;
          state.questions[itemNum+1].questions[0].active = true;
        }
      }
      state.answers.map(item => {
        if (item.id == questionId) {
          item.answer = payload.answer;
        }
      })
    } else if (payload.type == 'prev') {
      if (state.questions[itemNum].questions[questionNum-1]) {
        state.questions[itemNum].questions[questionNum-1].active = true;
        state.questions[itemNum].active = true;
      } else {
        state.questions[itemNum-1].active = true;
        state.questions[itemNum-1].questions[state.questions[itemNum-1].questions.length-1].active = true;
      }
    }
  },
}

export default {
  namespaced,
  actions,
  state,
  mutations,
};
